import React, { useEffect, useMemo, useState } from 'react';
import { request } from 'utils/api';
import { isVenueDateUnavailable } from 'utils/helpers/openingHours';
import {
  getContentfulAssetUrlandAlt,
  getContentfulField,
} from 'common/components';
import { getContentRides } from 'public/components/AttractionScreen/RideClosures';

import OperationalHoursModal from '../OperationalHoursModal/OperationalHoursModal';

// Same mapping ParkScreenHero/GetTickets use for a single venue, applied
// per-tab here so each bundle venue can show its own banner.
const getOpeningHoursBanner = (venue) => {
  const openingHoursBanner = getContentfulField(
    venue?.parkScreen?.openingHoursBanner
  );
  if (!openingHoursBanner) return null;

  const assetEntry = openingHoursBanner?.fields?.asset
    ? getContentfulField(openingHoursBanner.fields.asset)
    : null;

  return {
    name: getContentfulField(openingHoursBanner?.fields?.name),
    hideLinearGradient: getContentfulField(
      openingHoursBanner?.fields?.hideLinearGradient
    ),
    asset: assetEntry
      ? {
          name: getContentfulField(assetEntry?.fields?.name),
          mobile: assetEntry?.fields?.mobile
            ? getContentfulAssetUrlandAlt(assetEntry.fields.mobile)
            : null,
          tablet: assetEntry?.fields?.tablet
            ? getContentfulAssetUrlandAlt(assetEntry.fields.tablet)
            : null,
          desktop: assetEntry?.fields?.desktop
            ? getContentfulAssetUrlandAlt(assetEntry.fields.desktop)
            : null,
        }
      : null,
  };
};

// Link that opens the shared OperationalHoursModal with one tab per venue.
// Hours come from the venues API (by slug), not from Contentful.
export default function IconRowHoursLink({
  slugs,
  linkText,
  className,
  labels = [],
}) {
  const [venuesBySlug, setVenuesBySlug] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const slugKey = slugs.join(',');
  // Optional per-slug tab label override (index-aligned with `slugs`), so a
  // bundle's hours modal can show different tab text than the venue's own
  // `name` (used site-wide in headers, directory, checkout, etc.) without
  // changing that canonical name.
  const labelsKey = labels.join('|');

  useEffect(() => {
    let cancelled = false;
    slugKey.split(',').forEach((slug) => {
      request({ method: 'GET', path: `/1/venues/slug/${slug}` })
        .then((res) => {
          if (!cancelled && res?.data) {
            setVenuesBySlug((prev) => ({ ...prev, [slug]: res.data }));
          }
        })
        .catch((err) => console.error(`Failed to fetch venue ${slug}:`, err));
    });
    return () => {
      cancelled = true;
    };
  }, [slugKey]);

  const tabs = useMemo(() => {
    const labelOverrides = labelsKey ? labelsKey.split('|') : [];
    return slugKey
      .split(',')
      .map((slug, index) => ({ slug, index }))
      .filter(({ slug }) => venuesBySlug[slug])
      .map(({ slug, index }) => {
        const venue = venuesBySlug[slug];
        return {
          label: labelOverrides[index] || venue.name,
          openingHours: venue.openingHours,
          venueId: venue.id,
          openingHoursBanner: getOpeningHoursBanner(venue),
          ridesCards: getContentRides(venue) || [],
          isDateUnavailable: (isoDate) =>
            isVenueDateUnavailable(venue, isoDate),
        };
      });
  }, [slugKey, venuesBySlug, labelsKey]);

  if (!tabs.length) return null;

  return (
    <OperationalHoursModal
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={
        <button type="button" className={className}>
          {linkText}
        </button>
      }
      tabs={tabs}
      initialDate={selectedDate.toISOString()}
      updateSelectedDate={setSelectedDate}
    />
  );
}
